import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { spacing, colors } from '../../../styles';
import { OptionsHistoryResult } from './types';
import { SENTIMENT_COLORS } from './shared/constants';

interface ParsedToolOutput {
  type?: string;
  status?: string;
  message?: string;
  [key: string]: any;
}

interface ToolRendererProps {
  toolName: string;
  output: ParsedToolOutput;
  isLoading?: boolean;
}

const LoadingState: FC<{ message: string }> = ({ message }) => (
  <div
    css={css`
      font-size: 14px;
      color: ${SENTIMENT_COLORS.muted};
    `}
  >
    {message}
  </div>
);

export const OptionsHistoryRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  // Parse the output data
  const data = useMemo((): OptionsHistoryResult | null => {
    if (typeof output === 'string') {
      try {
        return JSON.parse(output) as OptionsHistoryResult;
      } catch {
        return null;
      }
    }
    return output as OptionsHistoryResult;
  }, [output]);

  if (isLoading) {
    return <LoadingState message="Analyzing historical options data..." />;
  }

  if (!data || data.error) {
    return <LoadingState message={data?.error || 'Analysis completed'} />;
  }

  // Helper functions
  const formatNumber = (value: number | null): string => {
    if (value === null) return 'N/A';
    return value.toLocaleString();
  };

  const formatPercent = (value: number | null): string => {
    if (value === null) return 'N/A';
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getTrendIcon = (trend: 'increasing' | 'decreasing' | 'stable'): string => {
    switch (trend) {
      case 'increasing':
        return '📈';
      case 'decreasing':
        return '📉';
      default:
        return '➡️';
    }
  };

  const getPercentileColor = (percentile: number | null): string => {
    if (percentile === null) return SENTIMENT_COLORS.muted;
    if (percentile >= 75) return SENTIMENT_COLORS.negative;
    if (percentile <= 25) return SENTIMENT_COLORS.positive;
    return SENTIMENT_COLORS.neutral;
  };

  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.liquorice};
        line-height: 1.6;
        padding: ${spacing.deci};

        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {/* Header Section */}
      <div
        css={css`
          margin-bottom: ${spacing.deci};
          padding-bottom: ${spacing.milli};
          border-bottom: 1px solid ${colors.cloud};

          html[data-theme='dark'] & {
            border-bottom-color: ${colors.tungsten};
          }
        `}
      >
        <div
          css={css`
            font-weight: 600;
            font-size: 16px;
            margin-bottom: ${spacing.milli};
          `}
        >
          {data.symbol} Historical Options Analysis
        </div>
        <div
          css={css`
            font-size: 12px;
            color: ${SENTIMENT_COLORS.muted};
          `}
        >
          {data.analysis_period?.start_date} to {data.analysis_period?.end_date} (
          {data.analysis_period?.trading_days_analyzed} trading days)
        </div>
      </div>

      {/* Insights Section */}
      {data.insights && data.insights.length > 0 && (
        <div
          css={css`
            background: ${colors.fog};
            padding: ${spacing.centi};
            border-radius: 6px;
            margin-bottom: ${spacing.deci};

            html[data-theme='dark'] & {
              background: var(--card-background);
            }
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            💡 Key Insights
          </div>
          <ul
            css={css`
              margin: 0;
              padding-left: ${spacing.deka};
              list-style: disc;
            `}
          >
            {data.insights.map((insight, idx) => (
              <li key={idx}>{insight}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Large Trades Section - PRIORITIZED */}
      {data.large_trades && data.large_trades.count > 0 && (
        <div
          css={css`
            background: ${colors.earthAir};
            padding: ${spacing.centi};
            border-radius: 6px;
            margin-bottom: ${spacing.deci};
            border: 2px solid ${colors.earth};

            html[data-theme='dark'] & {
              background: ${colors.tungsten};
              border-color: ${colors.earth};
            }
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
              font-size: 15px;
            `}
          >
            🔥 Large Trades ({data.large_trades.count} trades {'>'}$100k)
          </div>
          <div
            css={css`
              font-size: 12px;
              color: ${SENTIMENT_COLORS.muted};
              margin-bottom: ${spacing.centi};
            `}
          >
            Total Premium: ${(data.large_trades.summary.total_premium_traded / 1_000_000).toFixed(1)}M{' | '}
            Call/Put Ratio: {data.large_trades.summary.call_put_premium_ratio?.toFixed(2) || 'N/A'}
          </div>

          {/* Top Trades Table */}
          <div
            css={css`
              overflow-x: auto;
            `}
          >
            <table
              css={css`
                width: 100%;
                font-size: 12px;
                border-collapse: collapse;

                th {
                  text-align: left;
                  padding: ${spacing.milli};
                  border-bottom: 1px solid ${colors.cloud};
                  font-weight: 600;
                }

                td {
                  padding: ${spacing.milli};
                  border-bottom: 1px solid ${colors.cloud};
                }

                tr:hover {
                  background: rgba(0, 0, 0, 0.02);
                }

                html[data-theme='dark'] & {
                  th {
                    border-bottom-color: ${colors.tungsten};
                  }
                  td {
                    border-bottom-color: ${colors.tungsten};
                  }
                  tr:hover {
                    background: rgba(255, 255, 255, 0.05);
                  }
                }
              `}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Strike</th>
                  <th>Premium</th>
                  <th>Volume</th>
                  <th>IV</th>
                  <th>DTE</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.large_trades.top_trades.slice(0, 10).map((trade, idx) => (
                  <tr key={idx}>
                    <td>{trade.date}</td>
                    <td>
                      <span
                        css={css`
                          color: ${trade.type === 'PUT' ? SENTIMENT_COLORS.negative : SENTIMENT_COLORS.positive};
                          font-weight: 500;
                        `}
                      >
                        {trade.type}
                      </span>
                    </td>
                    <td>${trade.strike.toFixed(2)}</td>
                    <td
                      css={css`
                        font-weight: 600;
                      `}
                    >
                      ${(trade.premium / 1_000_000).toFixed(2)}M
                    </td>
                    <td>
                      {trade.volume.toLocaleString()}
                      {trade.vol_to_oi_ratio && trade.vol_to_oi_ratio > 5 && (
                        <span
                          css={css`
                            color: ${SENTIMENT_COLORS.info};
                            margin-left: ${spacing.milli};
                          `}
                        >
                          ({trade.vol_to_oi_ratio}x OI)
                        </span>
                      )}
                    </td>
                    <td>{trade.implied_volatility ? `${trade.implied_volatility.toFixed(1)}%` : 'N/A'}</td>
                    <td>{trade.days_to_expiry !== null ? `${trade.days_to_expiry}d` : 'N/A'}</td>
                    <td>
                      {trade.flags.short_dated && '⚡'}
                      {trade.flags.high_iv && '💰'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.large_trades.count > 10 && (
            <div
              css={css`
                font-size: 11px;
                color: ${SENTIMENT_COLORS.muted};
                margin-top: ${spacing.milli};
                text-align: center;
              `}
            >
              Showing top 10 of {data.large_trades.count} large trades
            </div>
          )}
        </div>
      )}

      {/* Call/Put Ratios Section */}
      {data.call_put_ratios && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            📊 Call/Put Ratios
          </div>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: ${spacing.centi};
            `}
          >
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Current (Volume)
              </div>
              <div
                css={css`
                  font-weight: 500;
                  color: ${getPercentileColor(data.call_put_ratios?.volume_percentile_rank ?? null)};
                `}
              >
                {formatNumber(
                  data.call_put_ratios?.current_volume_ratio ?? (data.call_put_ratios as any)?.latest?.ratio ?? null,
                )}{' '}
                {getTrendIcon(data.call_put_ratios?.trend ?? 'stable')}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Period Avg
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {formatNumber(data.call_put_ratios?.average_period ?? null)}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Percentile
              </div>
              <div
                css={css`
                  font-weight: 500;
                  color: ${getPercentileColor(data.call_put_ratios?.volume_percentile_rank ?? null)};
                `}
              >
                {data.call_put_ratios?.volume_percentile_rank != null
                  ? `${data.call_put_ratios.volume_percentile_rank}th`
                  : 'N/A'}
              </div>
            </div>
          </div>
          {data.call_put_ratios?.interpretation && (
            <div
              css={css`
                margin-top: ${spacing.milli};
                font-size: 13px;
                color: ${SENTIMENT_COLORS.info};
              `}
            >
              {data.call_put_ratios.interpretation}
            </div>
          )}
        </div>
      )}

      {/* Open Interest Section */}
      {data.open_interest && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            📈 Open Interest
          </div>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: ${spacing.centi};
            `}
          >
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Total OI
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {formatNumber(data.open_interest?.current_total_oi ?? null)}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Period Growth
              </div>
              <div
                css={css`
                  font-weight: 500;
                  color: ${data.open_interest?.growth_percent && data.open_interest.growth_percent > 0
                    ? SENTIMENT_COLORS.positive
                    : SENTIMENT_COLORS.negative};
                `}
              >
                {formatPercent(data.open_interest?.growth_percent ?? null)}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Call/Put Skew
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {formatNumber(data.open_interest?.call_put_skew ?? null)}
              </div>
            </div>
          </div>
          {data.open_interest?.interpretation && (
            <div
              css={css`
                margin-top: ${spacing.milli};
                font-size: 13px;
                color: ${SENTIMENT_COLORS.info};
              `}
            >
              {data.open_interest.interpretation}
            </div>
          )}
        </div>
      )}

      {/* Volume Analysis Section */}
      {data.volume_analysis && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            📊 Volume Analysis
          </div>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: ${spacing.centi};
            `}
          >
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Current Volume
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {formatNumber(data.volume_analysis?.current_volume ?? null)}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Average Volume
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {formatNumber(data.volume_analysis?.average_volume ?? null)}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                High Volume Days
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {data.volume_analysis?.high_volume_days_count ?? 'N/A'}
              </div>
            </div>
          </div>
          {data.volume_analysis?.high_volume_days && data.volume_analysis.high_volume_days.length > 0 && (
            <div
              css={css`
                margin-top: ${spacing.centi};
                font-size: 12px;
              `}
            >
              {data.volume_analysis.high_volume_days.slice(0, 3).map((day, idx) => (
                <div
                  key={idx}
                  css={css`
                    color: ${SENTIMENT_COLORS.muted};
                    margin-top: ${spacing.micro};
                  `}
                >
                  • {day.date}: {day.vs_average}x average volume
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Implied Volatility Section */}
      {data.implied_volatility && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            📉 Implied Volatility
          </div>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: ${spacing.centi};
            `}
          >
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Put IV
              </div>
              <div
                css={css`
                  font-weight: 500;
                  color: ${getPercentileColor(data.implied_volatility?.put_percentile_rank ?? null)};
                `}
              >
                {data.implied_volatility?.current_put_iv != null ? `${data.implied_volatility.current_put_iv}%` : 'N/A'}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Call IV
              </div>
              <div
                css={css`
                  font-weight: 500;
                  color: ${getPercentileColor(data.implied_volatility?.call_percentile_rank ?? null)};
                `}
              >
                {data.implied_volatility?.current_call_iv != null
                  ? `${data.implied_volatility.current_call_iv}%`
                  : 'N/A'}
              </div>
            </div>
            <div>
              <div
                css={css`
                  color: ${SENTIMENT_COLORS.muted};
                  font-size: 12px;
                `}
              >
                Trend
              </div>
              <div
                css={css`
                  font-weight: 500;
                `}
              >
                {getTrendIcon(data.implied_volatility?.trend ?? 'stable')} {data.implied_volatility?.trend ?? 'N/A'}
              </div>
            </div>
          </div>
          {data.implied_volatility?.interpretation && (
            <div
              css={css`
                margin-top: ${spacing.milli};
                font-size: 13px;
                color: ${SENTIMENT_COLORS.info};
              `}
            >
              {data.implied_volatility.interpretation}
            </div>
          )}
        </div>
      )}

      {/* Unusual Activity Section */}
      {(data.unusual_activity?.count ?? 0) > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 600;
              margin-bottom: ${spacing.milli};
            `}
          >
            🚨 Unusual Activity ({data.unusual_activity?.count} event{data.unusual_activity?.count !== 1 ? 's' : ''})
          </div>
          <div
            css={css`
              font-size: 12px;
            `}
          >
            {data.unusual_activity?.events?.slice(0, 5).map((event, idx) => (
              <div
                key={idx}
                css={css`
                  background: ${colors.earthAir};
                  padding: ${spacing.centi};
                  border-radius: 4px;
                  margin-bottom: ${spacing.milli};

                  html[data-theme='dark'] & {
                    background: ${colors.tungsten};
                  }
                `}
              >
                <div
                  css={css`
                    font-weight: 500;
                  `}
                >
                  {event.date} - {event.vs_average}x average volume
                </div>
                <div
                  css={css`
                    color: ${SENTIMENT_COLORS.muted};
                    margin-top: ${spacing.micro};
                  `}
                >
                  Type: {event.type?.replace('_', ' ') ?? 'unknown'} (P/C: {formatNumber(event.put_call_ratio)})
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata Footer */}
      <div
        css={css`
          margin-top: ${spacing.deka};
          padding-top: ${spacing.centi};
          border-top: 1px solid ${colors.cloud};
          font-size: 11px;
          color: ${SENTIMENT_COLORS.muted};

          html[data-theme='dark'] & {
            border-top-color: ${colors.tungsten};
          }
        `}
      >
        Sampling: {data.metadata?.sampling_method ?? 'unknown'}
      </div>
    </div>
  );
};
